import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    inject,
    input,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { HospTypeEnum } from 'src/app/compliance/api/enums/hosp-type-enum.enum';
import { RecruiterBonusComponent } from 'src/app/shared/components/recruiter-bonus/recruiter-bonus.component';
import { SkeletonLoaderDirective } from 'src/app/shared/components/skeleton-loader/skeleton-loader.directive';
import { WfdPriorityReturnerChipsViewComponent } from 'src/app/shared/components/wfd-priority-returner-chips/priority-returner-view/priority-returner-view.component';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { OffersUserType } from 'src/app/shared/models/enums/offers.enum';
import { Offer, OfferType, RTOApprovalStatuses } from 'src/app/shared/models/offers.model';
import { SanitizerPipe } from 'src/app/shared/pipes/sanitizer.pipe';
import { RtoAlertIconComponent } from 'src/app/shared/rto-alerts/rto-alert-icon/rto-alert-icon';
import { ToasterService } from 'src/app/shared/services/toaster.service';

@Owner('CodeBusters')
@Component({
    selector: 'nova-shared-offer-details-view',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatTooltipModule,
        MatCardModule,
        RecruiterBonusComponent,
        RtoAlertIconComponent,
        SkeletonLoaderDirective,
        WfdPriorityReturnerChipsViewComponent,
        SanitizerPipe
    ],
    templateUrl: './offer-details-view.component.html',
    styleUrl: './offer-details-view.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SharedOfferDetailsViewComponent {
    @ViewChild('offerDetailsContent') offerDetailsContent: ElementRef<HTMLElement>;

    @Input() offerData: Offer;
    @Input() showSampleMarginButton: boolean;
    @Input() showImportMarginButton: boolean;
    @Input() showCreateAndImportMarginButtons: boolean;
    @Input() showOfferMarginDifferences: boolean;
    @Input() displayedContractLengthWeeks: number;
    @Input() displayedContractLengthDays: number;
    @Input() endDateTextWithoutWeeks: string;
    @Input() showRTOAlertOnStartEndDates: boolean;
    @Input() jobProfessionName: string | null;
    @Input() isWfdReturner: boolean;
    @Input() wfdPriority: number;
    @Input() urlParent: string;
    readonly userType = input<OffersUserType>(undefined);

    @Output() goToJobId = new EventEmitter<number>();
    @Output() duplicateSampleMargin = new EventEmitter<void>();
    @Output() showImportMarginDialog = new EventEmitter<void>();
    @Output() createOfferMargin = new EventEmitter<void>();
    @Output() openMarginCalculator = new EventEmitter<number>();
    @Output() openMarginDifferences = new EventEmitter<void>();
    @Output() openFacility = new EventEmitter<number>();
    @Output() openUnitFormUrl = new EventEmitter<{ event: MouseEvent; unitFormId: number }>();
    @Output() goToContractRequest = new EventEmitter<{ contractRequestId: number; marginId: number }>();

    hospTypeEnum = HospTypeEnum;
    offerTypeEnum = OfferType;

    static readonly ignoreOfferDetailsLabels = [
        'Position Type',
        'Unit',
        'Candidate Profession',
        'Payroll Work Week',
        'Shift Details'
    ];

    private static readonly rtoLabel = 'RTO';
    private static readonly rtoRegex = /(\d)\s(\d)/g;
    private static readonly rtoRegexReplace = '$1; $2';

    private readonly _toasterService = inject(ToasterService);

    get rtoApprovalStatus(): string {
        switch (this.offerData?.rtoApprovalStatus) {
            case RTOApprovalStatuses.Approved: {
                return 'Approved';
            }
            case RTOApprovalStatuses.PendingApproval: {
                return 'Pending Approval';
            }
            default: {
                return 'Not Approved';
            }
        }
    }

    get disableSampleMargin(): boolean {
        return this.offerData?.sampleMarginId <= 0;
    }

    get sampleMarginTooltip(): string {
        return this.offerData?.sampleMarginId <= 0
            ? 'Unable to create Margin. Please contact the Account Manager to add a sample margin on the job before proceeding.'
            : '';
    }

    get facilityCityAndState(): string {
        return this.offerData?.facilityCity && this.offerData?.facilityState
            ? `(${this.offerData.facilityCity}, ${this.offerData.facilityState})`
            : '';
    }

    get requestedTimeOffDatesArray(): string[] {
        return this.offerData?.requestedTimeOffDateText ? this.offerData.requestedTimeOffDateText.split(';') : [];
    }

    get displayCopyButton(): boolean {
        const userType = this.userType();
        return userType !== undefined && userType !== OffersUserType.AccountManager;
    }

    async copyOfferDetailsToClipboard(event: MouseEvent) {
        event.preventDefault();
        if (!this.offerDetailsContent?.nativeElement) {
            return;
        }

        try {
            await this.formatOfferDetailsContentForClipboard();
            this._toasterService.toast('Copied Offer Summary');
        } catch {
            this._toasterService.toastErrors('Failed to copy Offer Summary');
        }
    }

    private async formatOfferDetailsContentForClipboard() {
        const rows = Array.from(this.offerDetailsContent.nativeElement.querySelectorAll('.content-row'));
        const htmlRows = [];
        const plainTextRows = [];

        rows.forEach((row) => {
            const label = row.querySelector('.content-label')?.textContent?.trim() || '';
            let value = row.querySelector('.content-value')?.textContent?.trim() || '';

            if (SharedOfferDetailsViewComponent.ignoreOfferDetailsLabels.includes(label)) {
                return;
            } else if (label.toUpperCase() === SharedOfferDetailsViewComponent.rtoLabel) {
                value = this.formatRtoValue(value);
            }

            htmlRows.push(`
                <tr>
                    <td style="padding-right: 10px;">${label}</td>
                    <td>${value}</td>
                </tr>
            `);
            plainTextRows.push(`${label}: ${value}`);
        });

        const htmlTable = `<table style="border-collapse: collapse;">${htmlRows.join('')}</table>`;
        const plainText = plainTextRows.join('\n');

        await navigator.clipboard.write([
            new ClipboardItem({
                'text/html': new Blob([htmlTable], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' })
            })
        ]);
    }

    private formatRtoValue(value: string): string {
        return value.replace(SharedOfferDetailsViewComponent.rtoRegex, SharedOfferDetailsViewComponent.rtoRegexReplace);
    }
}
